/**
 * One line icon per service category, plus one per booking type.
 *
 * Icons rather than photographs, deliberately. Ten category photos would be
 * ~2 MB to download, would need re-shooting every time a category changes, and
 * a stock photo of a smiling stranger says nothing about what the service is.
 * Line art loads instantly, inherits the theme, stays sharp at any size, and
 * reads the same in light and dark.
 *
 * Photography still matters — on companion profiles, where a real face builds
 * real trust. See docs/04-IMAGE-SPEC.md.
 *
 * All paths are drawn on a 24x24 grid with a 1.5 stroke and no fill, so they
 * sit together as one family.
 *
 * ICONS ARE KEYED BY PICTURE, NOT BY CATEGORY. They used to be keyed by slug,
 * which meant a category created by an admin at runtime matched nothing and
 * fell back to a generic circle. A category now stores which drawing it wants
 * (`icon_key`, migration 0020), so a new "Airport Companion" picks `suitcase`
 * and looks like it belongs. That is also why the keys below read `plate` and
 * `ticket` rather than `dining-partner` and `movie-event-partner`.
 */

const PATHS: Record<string, string> = {
  // Speech bubble with a reply
  'speech-bubble':
    'M8 10h8M8 13.5h5M4 5h16a1 1 0 011 1v9a1 1 0 01-1 1h-7l-5 4v-4H4a1 1 0 01-1-1V6a1 1 0 011-1z',

  // Screen with a presence dot
  'screen':
    'M3 5h18a1 1 0 011 1v9a1 1 0 01-1 1H3a1 1 0 01-1-1V6a1 1 0 011-1zM8 20h8M12 16v4M12 10.5h.01M9 10.5h.01M15 10.5h.01',

  // Two hands, one supporting the other
  'hands-heart':
    'M12 20s-6.5-4.2-6.5-8.5A3.8 3.8 0 0112 8.7a3.8 3.8 0 016.5 2.8C18.5 15.8 12 20 12 20zM8.5 12.5l2 2 4-4',

  // Ball and racket — activity, generic
  'racket':
    'M9 15a6 6 0 100-12 6 6 0 000 12zM9 3v12M3 9h12M8 15l-4 4a1.5 1.5 0 002 2l4-4M18 14v7M15.5 16.5h5',

  // Plate with cutlery
  'plate':
    'M12 20a6 6 0 100-12 6 6 0 000 12zM5 3v7a2 2 0 004 0V3M7 10v10M19 3c-1.5 0-2.5 2-2.5 4.5S17.5 11 19 11m0-8v18',

  // Ticket
  'ticket':
    'M3 8a2 2 0 002-2h14a2 2 0 002 2v1.5a2.5 2.5 0 000 5V16a2 2 0 01-2 2H5a2 2 0 01-2-2v-1.5a2.5 2.5 0 000-5zM10 6v2M10 11v2M10 16v2',

  // Skyline with a pin
  'skyline':
    'M3 21h18M5 21V10l4-3v14M13 21V6l6 4v11M8 13h.01M8 16h.01M16 13h.01M16 16h.01',

  // Suitcase
  'suitcase':
    'M4 8h16a1 1 0 011 1v10a1 1 0 01-1 1H4a1 1 0 01-1-1V9a1 1 0 011-1zM9 8V5a1 1 0 011-1h4a1 1 0 011 1v3M3 13h18',

  // Shopping bag
  'shopping-bag': 'M5 8h14l-1 12H6L5 8zM9 8V6a3 3 0 016 0v2',

  // Two figures side by side
  'two-figures':
    'M9 11a3 3 0 100-6 3 3 0 000 6zM3 20v-1.5A4.5 4.5 0 017.5 14h3a4.5 4.5 0 014.5 4.5V20M17 5.5a3 3 0 010 5.8M18.5 14a4.5 4.5 0 013 4.2V20',
};

const BOOKING_PATHS: Record<string, string> = {
  chat: 'M4 5h16a1 1 0 011 1v9a1 1 0 01-1 1h-7l-5 4v-4H4a1 1 0 01-1-1V6a1 1 0 011-1z',
  voice:
    'M6.5 3h2l1.5 4-2 1.5a12 12 0 005.5 5.5L15 12l4 1.5v2a2.5 2.5 0 01-2.7 2.5A14.5 14.5 0 014 7.7 2.5 2.5 0 016.5 3z',
  video: 'M3 7h11a1 1 0 011 1v8a1 1 0 01-1 1H3a1 1 0 01-1-1V8a1 1 0 011-1zM15 11l6-3.5v9L15 13',
  in_person:
    'M12 3a4.5 4.5 0 014.5 4.5c0 3.2-4.5 8-4.5 8s-4.5-4.8-4.5-8A4.5 4.5 0 0112 3zM12 7.5h.01M5 20h14',
};

const FALLBACK = 'M12 3a9 9 0 100 18 9 9 0 000-18zM12 8v4M12 16h.01';

function Glyph({
  d,
  size,
  className,
}: {
  d: string;
  size: number;
  className: string;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

export default function ServiceIcon({
  iconKey,
  size = 24,
  className = '',
}: {
  iconKey: string;
  size?: number;
  className?: string;
}) {
  return <Glyph d={PATHS[iconKey] ?? FALLBACK} size={size} className={className} />;
}

export function BookingTypeIcon({
  type,
  size = 16,
  className = '',
}: {
  type: string;
  size?: number;
  className?: string;
}) {
  return <Glyph d={BOOKING_PATHS[type] ?? FALLBACK} size={size} className={className} />;
}

/**
 * The drawings an admin can choose from, in picker order. This is the source
 * of truth for what `service_categories.icon_key` may hold — deliberately the
 * app and not a database table, because the set is bounded by what is actually
 * drawn above, and a table would drift from it the first time an icon is added
 * or removed. An unknown key renders FALLBACK rather than breaking the page.
 */
export const ICON_KEYS = Object.keys(PATHS);

/**
 * The accent styles for a category, from its stored hue.
 *
 * Saturation and lightness are fixed here rather than stored, so a hue chosen
 * in the admin form cannot produce an unreadable card — the worst an admin can
 * do is pick a colour they do not like, not one nobody can see. Three pages
 * and the admin preview all read from this, so they cannot drift apart.
 */
export function categoryTint(hue: number): { background: string; color: string } {
  return {
    background: `hsl(${hue} 62% 50% / 0.12)`,
    color: `hsl(${hue} 58% 42%)`,
  };
}
